<template>
	<main class="text-page">
		<div class="text-page__bg _ibg"></div>
		<div class="text-page__inner _cnt">
			<div class="section text-section">
				<div class="page-inside-top">
					<Transition>
						<div v-if="pageContent" class="wrapper">
							<div class="text-page__title _h2">
								{{ pageContent.title }}
							</div>
							<div
								class="text-page__header"
								v-html="pageContent.header"
							/>
							<div
								class="text-page__content"
								v-html="pageContent.content"
							/>
						</div>
					</Transition>
				</div>
			</div>
		</div>
	</main>
</template>

<script>
import { mapActions, mapState } from 'vuex';

export default {
	name: 'TextPage',
	data() {
		return {
			pageContent: null,
			loading: false,
		};
	},
	computed: {
		...mapState('app', ['currentLanguage']),
	},
	watch: {
		'$route.params.slug': {
			immediate: true,
			handler(newSlug) {
				if (newSlug) {
					this.loadPageContent();
				}
			},
		},
		currentLanguage: {
			handler() {
				if (this.$route.params.slug) {
					this.loadPageContent();
				}
			},
		},
	},
	methods: {
		...mapActions('staticPages', ['fetchPageContent']),
		async loadPageContent() {
			try {
				this.loading = true;
				this.pageContent = null; 

				const langId = this.currentLanguage;

				const slug = this.$route.params.slug;
				const content = await this.fetchPageContent({ slug, langId });
				this.pageContent = content;

				// ✅ ADD THIS
				this.$nextTick(() => {
					this.markNumberedParagraphs();
				});
			} catch (error) {
				console.error('Failed to load page content:', error);
			} finally {
				this.loading = false;
			}
		},

		markNumberedParagraphs() {
			const root = this.$el.querySelector('.text-page__content');
			if (!root) return;

			root.querySelectorAll('p').forEach((p) => {
				// Walk through all text nodes inside the <p>
				Array.from(p.childNodes).forEach((node) => {
					if (node.nodeType !== Node.TEXT_NODE) return;

					const raw = (node.textContent || '').replace(/\s+/g, ' ').trim();
					if (!raw) return;

					// remove leading spaces, NBSPs, quotes, «», etc.
					const cleaned = raw.replace(/^[\s\u00A0"'«»]+/, '');

					// If this text line looks like "1. Something", "6. Limitation...", etc.
					if (/^\d+\./.test(cleaned)) {
						const titleEl = document.createElement('div');
						titleEl.className = 'text-page__section-title';
						titleEl.textContent = cleaned;

						// insert the new title element before the original text node
						p.insertBefore(titleEl, node);

						// clear original text node (we’ve moved its content to titleEl)
						node.textContent = '';
					}
				});
			});
		},
		back() {
			this.$router.go(-1);
		},
	},
};
</script>

<style lang="scss" scoped>
@use '@/assets/styles/mixins' as *;
@use '@/assets/styles/media' as *;
@use '@/assets/styles/components/classes' as *;

.text-page {
	@include header-indent;
	@include adaptiveValue('padding-top', 60, 25);
	@include adaptiveValue('padding-bottom', 60, 25);
	position: relative;
	min-height: calc(100vh - 98px);
	@media (max-width: $md1) {
		min-height: calc(100vh - 70px);
	}
	@media (max-width: $md4) {
		min-height: calc(100vh - 50px);
	}
	&::before {
		content: '';
		position: fixed;
		width: 100%;
		height: 100%;
		top: 0;
		left: 0;
		background-image: url('@/assets/images/bg/vertical-bg.png');
		background-repeat: no-repeat;
		background-size: contain;
		background-size: 100% 100%;
		z-index: -1;
	}
	&__title {
		text-align: center;
		&:not(:last-child) {
			margin-bottom: 10px;
		}
	}
	&__line {
		max-width: 710px;
		margin-left: auto;
		margin-right: auto;
		&:not(:last-child) {
			@include adaptiveValue('margin-bottom', 30, 20);
		}
	}
	&__header {
		color: var(--primary-color);
	}
	&__content {
		color: var(--secondary-color);
		font-weight: 400;
		:deep(a) {
			color: var(--hint-color);
			transition: color 0.3s ease 0s;
			@media (any-hover: hover) {
				&:hover {
					color: var(--hint-hover-color);
				}
			}
		}
	}
}

.text-page :deep(.text-page__section-title) {
	color: var(--primary-color);
	font-weight: 700;
	display: block;
}
.text-page strong {
	font-weight: bold;
}

.text-page em {
	font-style: italic;
}

.text-page ul {
	list-style: disc;
	padding-left: 30px;
	margin-bottom: 15px;
}

.text-page ol {
	list-style: decimal;
	padding-left: 30px;
	margin-bottom: 15px;
}

.text-page li {
	margin-bottom: 8px;
}

.text-page hr {
	border: 0;
	border-top: 1px solid var(--bg-third-color);
	margin: 20px 0;
}

.text-page th,
.text-page td {
	border-top: 1px solid var(--bg-third-color);
	padding: 8px;
	text-align: left;
}

.text-page :deep(.text-page-header p),
.text-page :deep(.text-page__content p) {
	margin-bottom: 15px;
	line-height: 1.2;
}

.text-page :deep(.text-page-header strong),
.text-page :deep(.text-page__content strong) {
	font-weight: bold;
}

.text-page :deep(.text-page-header em),
.text-page :deep(.text-page__content em) {
	font-style: italic;
}

.text-page :deep(.text-page-header ul),
.text-page :deep(.text-page__content ul) {
	list-style: disc;
	padding-left: 30px;
	margin-bottom: 15px;
}

.text-page :deep(.text-page-header ol),
.text-page :deep(.text-page__content ol) {
	list-style: decimal;
	padding-left: 30px;
	margin-bottom: 15px;
}

.text-page :deep(.text-page-header li),
.text-page :deep(.text-page__content li) {
	margin-bottom: 8px;
}

.text-page :deep(.text-page-header h1),
.text-page :deep(.text-page__content h1) {
	font-size: 2.2em;
	margin-bottom: 20px;
	font-weight: bold;
}

.text-page :deep(.text-page-header h2),
.text-page :deep(.text-page__content h2) {
	font-size: 1.8em;
	margin-bottom: 18px;
	font-weight: bold;
}

.text-page :deep(.text-page-header h3),
.text-page :deep(.text-page__content h3) {
	font-size: 1.5em;
	margin-bottom: 16px;
	font-weight: bold;
}

.text-page :deep(.text-page-header h4),
.text-page :deep(.text-page__content h4) {
	font-size: 1.3em;
	margin-bottom: 14px;
	font-weight: bold;
}

.text-page :deep(.text-page-header h5),
.text-page :deep(.text-page__content h5) {
	font-size: 1.1em;
	margin-bottom: 12px;
	font-weight: bold;
}

.text-page :deep(.text-page-header h6),
.text-page :deep(.text-page__content h6) {
	font-size: 1em;
	margin-bottom: 10px;
	font-weight: bold;
}

.text-page :deep(.text-page-header blockquote),
.text-page :deep(.text-page__content blockquote) {
	border-left: 4px solid var(--bg-third-color);
	padding-left: 20px;
	margin: 20px 0;
	font-style: italic;
}

.text-page :deep(.text-page-header hr),
.text-page :deep(.text-page__content hr) {
	border: 0;
	border-top: 1px solid var(--bg-third-color);
	margin: 20px 0;
}

.text-page :deep(.text-page-header table),
.text-page :deep(.text-page__content table) {
	width: 100%;
	border-collapse: collapse;
	margin: 20px 0;
}

.text-page :deep(.text-page-header th),
.text-page :deep(.text-page__content th),
.text-page :deep(.text-page-header td),
.text-page :deep(.text-page__content td) {
	border: 1px solid var(--bg-third-color);
	padding: 8px;
	text-align: left;
}
</style>
